import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { RevaluationTypeLabels } from 'further-ui/labels';
import {
  dateToLabel,
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { sumBy } from 'lodash';
import { ProcessedRevaluation } from '../../helpers/processRevaluationHistory';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

type RevaluationHistoryTableProps = {
  revaluations: Array<ProcessedRevaluation>;
  removeColumns?: Array<RemoveColumns>;
  showSummary?: boolean;
};

type RemoveColumns =
  | 'holdingClassName'
  | 'function'
  | 'type'
  | 'date'
  | 'valuationChangePercentage';

const RevaluationHistoryTable: React.FC<RevaluationHistoryTableProps> = ({
  revaluations,
  removeColumns,
  showSummary = true,
}) => {
  const moreThanOneCurrency =
    revaluations.length > 1 &&
    revaluations.some(
      (rev) =>
        rev.currency !== undefined &&
        revaluations[0].currency !== undefined &&
        rev.currency !== revaluations[0].currency,
    );

  const formattedRows = revaluations.map((rev, index) => ({
    ...rev,
    id: rev.id ?? `${index}-${rev.holdingClassId}`,
    priorValuation:
      rev.priorValuation !== undefined
        ? numberToCurrencyString(rev.priorValuation, {
            currency: rev.currency,
          })
        : '-',
    newValuation: numberToCurrencyString(rev.newValuation, {
      currency: rev.currency,
    }),
    priorUnitPrice:
      rev.priorUnitPrice !== undefined
        ? numberToCurrencyString(rev.priorUnitPrice, {
            currency: rev.currency,
          })
        : '-',
    newUnitPrice: numberToCurrencyString(rev.newUnitPrice, {
      currency: rev.currency,
      unlimitedDp: true,
    }),
    valuationChange:
      rev.valuationChange !== undefined
        ? numberToCurrencyString(rev.valuationChange, {
            currency: rev.currency,
          })
        : '-',
    valuationChangePercentage:
      rev.valuationChangePercentage !== undefined
        ? numberToPercentageString(rev.valuationChangePercentage)
        : '-',
  }));

  const priorValuationTotal = sumBy(revaluations, 'priorValuation');
  const newValuationTotal = sumBy(revaluations, 'newValuation');
  const valuationChangeTotal = sumBy(revaluations, 'valuationChange');

  const totalPercentageChange =
    (newValuationTotal - priorValuationTotal) / priorValuationTotal;

  type RowType = (typeof formattedRows)[number];

  const columns: Array<TableColumn<RowType>> = [
    {
      label: 'Holding class',
      key: 'holdingClassName',
      render: ({ holdingClassName }) => (
        <ColorIndicatorWithText text={holdingClassName} />
      ),
      footer: moreThanOneCurrency || !showSummary ? undefined : 'Totals',
    },
    {
      label: 'Valuation date',
      key: 'date',
      render: (row: RowType) => {
        return dateToLabel(row.date);
      },
    },
    {
      label: 'Function',
      key: 'type',

      render: (row: RowType) => {
        return row.type ? RevaluationTypeLabels[row.type] : '-';
      },
    },
    {
      label: 'Prior valuation',
      key: 'priorValuation',

      footer:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(priorValuationTotal),
    },
    {
      label: 'New valuation',
      key: 'newValuation',

      footer:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(newValuationTotal),
    },
    {
      label: 'Prior unit price',
      key: 'priorUnitPrice',
    },
    {
      label: 'New unit price',
      key: 'newUnitPrice',
    },
    {
      label: 'Valuation change',
      key: 'valuationChange',

      footer:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(valuationChangeTotal),
    },
    {
      label: 'Valuation change (%)',
      key: 'valuationChangePercentage',

      footer:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToPercentageString(totalPercentageChange),
    },
  ].filter((column) => !removeColumns?.includes(column.key as RemoveColumns));

  return <Table rows={formattedRows} columns={columns} />;
};

export default RevaluationHistoryTable;
